// Whitelist sanitizer for rich-HTML clinical notes (Plato letters/casenotes
// are Froala-editor output: nested divs with inline styles, spans, tables and
// S3-hosted images). No external dependency — DOMParser never executes
// scripts, and everything not explicitly allowed below is stripped or
// unwrapped before the result is injected via dangerouslySetInnerHTML.

const ALLOWED_TAGS = new Set([
  "div", "p", "br", "span", "strong", "b", "em", "i", "u", "s", "strike",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
  "table", "thead", "tbody", "tfoot", "tr", "td", "th",
  "blockquote", "hr", "a", "img", "sub", "sup", "pre", "code",
  "center", "small", "big", "font",
]);

// Removed together with their content — nothing inside is user-visible prose.
const DROP_TAGS = new Set([
  "script", "style", "iframe", "object", "embed", "form", "input", "select",
  "textarea", "button", "link", "meta", "title", "base", "svg", "math",
  "video", "audio", "source", "canvas", "noscript", "template", "dialog",
]);

// Presentation-only CSS properties; anything that could reposition content
// outside its container (position, transform, z-index…) or fetch resources
// (background-image via url()) is rejected.
const ALLOWED_STYLE_PROPS = new Set([
  "text-align", "font-size", "font-weight", "font-style", "text-decoration",
  "text-decoration-line", "color", "background-color", "width", "height",
  "margin", "margin-left", "margin-right", "margin-top", "margin-bottom",
  "padding", "padding-left", "padding-right", "padding-top", "padding-bottom",
  "border", "border-collapse", "line-height", "vertical-align", "white-space",
  "list-style-type",
]);

/** Cheap check: does this note content look like markup rather than plain text? */
export function looksLikeHtml(value) {
  return /<([a-z][a-z0-9]*)\b[^>]*>/i.test(String(value || ""));
}

function sanitizeStyle(raw) {
  return String(raw)
    .split(";")
    .map((declaration) => {
      const idx = declaration.indexOf(":");
      if (idx < 0) return null;
      const prop = declaration.slice(0, idx).trim().toLowerCase();
      const value = declaration.slice(idx + 1).trim();
      if (!prop || !value || !ALLOWED_STYLE_PROPS.has(prop)) return null;
      if (/url\s*\(|expression|javascript:/i.test(value)) return null;
      return `${prop}: ${value}`;
    })
    .filter(Boolean)
    .join("; ");
}

function isSafeUrl(value) {
  return /^https?:\/\//i.test(String(value || "").trim());
}

function sanitizeElementAttributes(element, tag) {
  for (const attr of [...element.attributes]) {
    const name = attr.name.toLowerCase();
    if (name === "style") {
      const cleaned = sanitizeStyle(attr.value);
      if (cleaned) element.setAttribute("style", cleaned);
      else element.removeAttribute(attr.name);
      continue;
    }
    if (name === "href" && tag === "a" && isSafeUrl(attr.value)) continue;
    if (name === "src" && tag === "img" && isSafeUrl(attr.value)) continue;
    if ((name === "colspan" || name === "rowspan") && (tag === "td" || tag === "th")) continue;
    if (name === "width" && tag === "img" && /^\d+%?$/.test(attr.value.trim())) continue;
    element.removeAttribute(attr.name);
  }
  if (tag === "a") {
    if (!element.getAttribute("href")) {
      // Anchor without a safe destination — keep the text, drop link affordance.
      element.removeAttribute("href");
    } else {
      element.setAttribute("target", "_blank");
      element.setAttribute("rel", "noopener noreferrer");
    }
  }
  if (tag === "img") {
    element.setAttribute("loading", "lazy");
    element.setAttribute("referrerpolicy", "no-referrer");
  }
}

function sanitizeChildren(node) {
  let child = node.firstChild;
  while (child) {
    let next = child.nextSibling;
    if (child.nodeType === Node.COMMENT_NODE) {
      child.remove();
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();
      if (DROP_TAGS.has(tag)) {
        child.remove();
      } else if (!ALLOWED_TAGS.has(tag)) {
        // Unknown tag: unwrap — hoist its children into place and continue
        // the walk from the first hoisted node so they get sanitized too.
        const firstHoisted = child.firstChild;
        while (child.firstChild) {
          node.insertBefore(child.firstChild, child);
        }
        child.remove();
        if (firstHoisted) next = firstHoisted;
      } else {
        sanitizeElementAttributes(child, tag);
        sanitizeChildren(child);
      }
    }
    child = next;
  }
}

/**
 * Sanitize untrusted note HTML down to presentational markup. Returns a string
 * safe to inject with dangerouslySetInnerHTML.
 */
export function sanitizeNoteHtml(html) {
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  sanitizeChildren(doc.body);
  return doc.body.innerHTML;
}
